import React, { useState } from 'react';
import { StudyPlanTask } from '../../types/study-plan';

interface DailyTaskViewProps {
  tasks: StudyPlanTask[];
}

const DailyTaskView: React.FC<DailyTaskViewProps> = ({ tasks }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Group tasks by date
  const groupTasksByDate = () => {
    const grouped: { [key: string]: StudyPlanTask[] } = {};
    
    tasks.forEach(task => {
      const dateKey = task.date.toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(task);
    });

    // Sort tasks within each day by time slot
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        const timeSlotOrder = ['early-morning', 'morning', 'late-morning', 'pre-noon', 'afternoon', 'late-afternoon', 'evening', 'night', 'late-night'];
        return timeSlotOrder.indexOf(a.timeSlot) - timeSlotOrder.indexOf(b.timeSlot);
      });
    });

    return grouped;
  };

  const groupedTasks = groupTasksByDate();
  const availableDates = Object.keys(groupedTasks).sort();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'study': return 'fa-book';
      case 'practice': return 'fa-pen';
      case 'revision': return 'fa-redo';
      default: return 'fa-tasks';
    }
  };

  const getTimeSlotDisplay = (slotId: string) => {
    const timeSlots: { [key: string]: { label: string; time: string } } = {
      'early-morning': { label: 'Early Morning', time: '05:00 - 07:00' },
      'morning': { label: 'Morning', time: '07:00 - 09:00' },
      'late-morning': { label: 'Late Morning', time: '09:00 - 11:00' },
      'pre-noon': { label: 'Pre Noon', time: '11:00 - 13:00' },
      'afternoon': { label: 'Afternoon', time: '13:00 - 15:00' },
      'late-afternoon': { label: 'Late Afternoon', time: '15:00 - 17:00' },
      'evening': { label: 'Evening', time: '17:00 - 19:00' },
      'night': { label: 'Night', time: '19:00 - 21:00' },
      'late-night': { label: 'Late Night', time: '21:00 - 23:00' }
    };
    return timeSlots[slotId] || { label: slotId, time: '' };
  };

  const calculateDayStats = (tasks: StudyPlanTask[]) => {
    const totalTime = tasks.reduce((sum, task) => sum + task.duration, 0);
    const subjects = Array.from(new Set(tasks.map(task => task.subject)));
    const highPriorityCount = tasks.filter(task => task.priority === 'high').length;
    
    return { totalTime, subjects: subjects.length, highPriorityCount };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="daily-task-view">
      {/* Date Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-primary">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">
                <i className="fas fa-calendar-day me-2"></i>
                Select Date to View Tasks
              </h6>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                {availableDates.map(dateString => {
                  const date = new Date(dateString);
                  const isSelected = date.toDateString() === selectedDate.toDateString();
                  const dayTasks = groupedTasks[dateString];
                  const stats = calculateDayStats(dayTasks);
                  
                  return (
                    <button
                      key={dateString}
                      className={`btn ${isSelected ? 'btn-primary' : 'btn-outline-primary'} position-relative`}
                      onClick={() => setSelectedDate(date)}
                    >
                      <div className="text-center">
                        <div className="fw-bold">
                          {date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="small">
                          {date.getDate()}
                        </div>
                        {stats.highPriorityCount > 0 && (
                          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                            {stats.highPriorityCount}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Day Tasks */}
      {availableDates.length > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-primary">
                    <i className="fas fa-tasks me-2"></i>
                    {formatDate(selectedDate.toDateString())}
                  </h5>
                  
                  {groupedTasks[selectedDate.toDateString()] && (
                    <div className="text-end">
                      <div className="small text-muted">
                        {(() => {
                          const stats = calculateDayStats(groupedTasks[selectedDate.toDateString()]);
                          return (
                            <>
                              <div><strong>{stats.totalTime}</strong> minutes total</div>
                              <div><strong>{stats.subjects}</strong> subjects</div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="card-body">
                {groupedTasks[selectedDate.toDateString()] ? (
                  <div className="row g-3">
                    {groupedTasks[selectedDate.toDateString()].map((task, index) => {
                      const timeSlot = getTimeSlotDisplay(task.timeSlot);
                      return (
                        <div key={task.id} className="col-md-6 col-lg-4">
                          <div className={`card border-${getPriorityColor(task.priority)} h-100`}>
                            <div className={`card-header bg-${getPriorityColor(task.priority)} bg-opacity-10 border-${getPriorityColor(task.priority)}`}>
                              <div className="d-flex justify-content-between align-items-center">
                                <small className={`fw-bold text-${getPriorityColor(task.priority)}`}>
                                  {timeSlot.label}
                                </small>
                                <span className={`badge bg-${getPriorityColor(task.priority)}`}>
                                  {task.priority}
                                </span>
                              </div>
                              <small className="text-muted d-block">{timeSlot.time}</small>
                            </div>
                            
                            <div className="card-body">
                              <div className="d-flex align-items-center mb-2">
                                <i className={`fas ${getTypeIcon(task.type)} text-${getPriorityColor(task.priority)} me-2`}></i>
                                <h6 className="mb-0 fw-bold">{task.subject}</h6>
                              </div>
                              
                              <p className="card-text mb-2">
                                <strong>Chapter:</strong> {task.chapter}
                              </p>
                              
                              <p className="card-text mb-2">
                                <strong>Topic:</strong> {task.topic}
                              </p>
                              
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  <i className="fas fa-clock me-1"></i>
                                  {task.duration} minutes
                                </small>
                                <span className={`badge bg-${getPriorityColor(task.priority)} bg-opacity-20 text-${getPriorityColor(task.priority)}`}>
                                  {task.type}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="text-muted">
                      <i className="fas fa-calendar-times fa-3x mb-3"></i>
                      <h5>No tasks scheduled for this date</h5>
                      <p>Select another date to view your study tasks</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      {availableDates.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-info">
              <div className="card-header bg-info text-white">
                <h6 className="mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  Weekly Summary
                </h6>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-3">
                    <div className="border-end">
                      <h4 className="text-primary mb-1">
                        {Object.values(groupedTasks).reduce((sum, dayTasks) => sum + dayTasks.length, 0)}
                      </h4>
                      <small className="text-muted">Total Tasks</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border-end">
                      <h4 className="text-success mb-1">
                        {Math.round(Object.values(groupedTasks).reduce((sum, dayTasks) => 
                          sum + dayTasks.reduce((taskSum, task) => taskSum + task.duration, 0), 0) / 60)}
                      </h4>
                      <small className="text-muted">Total Hours</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border-end">
                      <h4 className="text-warning mb-1">
                        {Array.from(new Set(Object.values(groupedTasks).flat().map(task => task.subject))).length}
                      </h4>
                      <small className="text-muted">Subjects Covered</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <h4 className="text-danger mb-1">
                      {Object.values(groupedTasks).reduce((sum, dayTasks) => 
                        sum + dayTasks.filter(task => task.priority === 'high').length, 0)}
                    </h4>
                    <small className="text-muted">High Priority</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyTaskView;